import React, { useEffect, useRef, useState } from 'react';
import { aiPlayersConfig } from '../config/ai-players-config';
import { calculateAIMove, STRATEGIES, getPlayerId } from '../config/oracle-ai';
import { checkWin } from '../config/patterns';
import { initializeGameLogging, logMove, completeGameLogging } from '../utils/gameLogger';

const DEFAULT_BOARD_SIZE = 7;
const DEFAULT_PATTERN = 'skinny';
const PATTERN_SETS = {
  tetrominoes: ['boxy', 'elly', 'knobby', 'tippy'],
  pentominoes: ['aspy', 'cross', 'tee', 'f', 'stair', 'p', 'l', 'i'],
  hexominoes: ['snaky', 'y', 'u', 'c', 's']
};
const K_FACTOR = 48;

const cloneBoard = (board) => board.map(row => [...row]);

function mapPlayerToStrategy(playerIdStr, _level) {
  // Use per-player aiMethod when available; otherwise default to vonNeumann
  return STRATEGIES.NASH || 'nash';
}

function expectedScore(ratingA, ratingB) {
  // Standard Elo expectation using rating difference
  return 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
}

async function playSingleGame(playerX, playerO, boardSize, pattern, gameId = null, tag = null) {
  // Returns { result: 'X'|'O'|'draw', plies, winnerName }
  let board = Array(boardSize).fill(null).map(() => Array(boardSize).fill(null));
  let current = 'X';
  let plies = 0;
  const maxPlies = boardSize * boardSize + 10;
  const getCfg = (p) => (p === 'X' ? playerX : playerO);
  
  // Initialize game logging if gameId provided
  if (gameId) {
    initializeGameLogging({
      boardSize,
      patternId: pattern,
      gameMode: 'computer-computer',
      gameSource: 'tournament',
      tag,
      player1Type: 'ai',
      player2Type: 'ai',
      player1Key: playerX.id,
      player2Key: playerO.id,
      player1TimeBudget: playerX.timeBudgetMs || 200,
      player2TimeBudget: playerO.timeBudgetMs || 200
    });
  }
  
  while (plies < maxPlies) {
    const cfg = getCfg(current);
    const strategy = cfg?.aiMethod || 'nash';
    // Pass playerKey to ensure correct Nash config per player
    const move = await calculateAIMove(board, pattern, getPlayerId(cfg.id), boardSize, strategy, plies === 0, 0, cfg.id);
    if (!move || board[move.row][move.col] !== null) {
      // Invalid/no move: treat as draw
      if (gameId) {
        await completeGameLogging('draw', plies);
      }
      return { result: 'draw', plies, winnerName: 'Draw' };
    }
    
    // Log the move if game logging is enabled
    if (gameId) {
      logMove({
        player: current,
        row: move.row,
        col: move.col,
        playerKey: cfg.id,
        boardState: board,
        availableMoves: null, // Could calculate if needed
        threatMatrixX: null, // Could calculate if needed
        threatMatrixO: null, // Could calculate if needed
        evaluationScore: null // Could calculate if needed
      });
    }
    
    const nextBoard = cloneBoard(board);
    nextBoard[move.row][move.col] = current;
    // Check win
    const win = checkWin(nextBoard, pattern, current, boardSize);
    if (win) {
      const winnerName = (current === 'X') ? playerX.name : playerO.name;
      if (gameId) {
        await completeGameLogging(current, plies + 1);
      }
      return { result: current, plies: plies + 1, winnerName };
    }
    // Check draw
    const isDraw = nextBoard.every(row => row.every(cell => cell !== null));
    if (isDraw) {
      if (gameId) {
        await completeGameLogging('draw', plies + 1);
      }
      return { result: 'draw', plies: plies + 1, winnerName: 'Draw' };
    }
    // Continue
    board = nextBoard;
    current = current === 'X' ? 'O' : 'X';
    plies += 1;
  }
  if (gameId) {
    await completeGameLogging('draw', plies);
  }
  return { result: 'draw', plies, winnerName: 'Draw' };
}

export default function Tournament({ onBack }) {
  const [boardSize, setBoardSize] = useState(DEFAULT_BOARD_SIZE);
  const [pattern, setPattern] = useState(DEFAULT_PATTERN);
  const [patternSet, setPatternSet] = useState('');
  const [gamesPerPair, setGamesPerPair] = useState(4);
  const [enableGameLogging, setEnableGameLogging] = useState(false);
  const [logTag, setLogTag] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [ratings, setRatings] = useState(() => {
    const initial = {};
    aiPlayersConfig.forEach(p => { initial[p.id] = 1500; });
    return initial;
  });
  const [results, setResults] = useState([]);
  const [elapsedMs, setElapsedMs] = useState(0);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    return () => {
      // Signal any running loop to stop scheduling further games
      cancelRef.current = true;
    };
  }, []);

  const runTournament = async () => {
    cancelRef.current = false;
    const players = aiPlayersConfig;
    const totalPairs = players.length * (players.length - 1) / 2;
    const total = totalPairs * gamesPerPair;
    setProgress({ done: 0, total });
    setIsRunning(true);
    setElapsedMs(0);
    const r = { ...ratings };
    const res = [];
    // Build randomized schedule of individual games with random first player each time
    const schedule = [];
    for (let i = 0; i < players.length; i++) {
      for (let j = i + 1; j < players.length; j++) {
        for (let g = 0; g < gamesPerPair; g++) {
          const xIsA = Math.random() < 0.5; // randomize who starts each game
          // Choose pattern for this game: from selected set if provided, else current single pattern
          let gamePattern = pattern;
          if (patternSet && PATTERN_SETS[patternSet] && PATTERN_SETS[patternSet].length > 0) {
            const setList = PATTERN_SETS[patternSet];
            gamePattern = setList[Math.floor(Math.random() * setList.length)];
          }
          schedule.push({ i, j, gameIndex: g, xIsA, gamePattern });
        }
      }
    }
    // Fisher-Yates shuffle
    for (let k = schedule.length - 1; k > 0; k--) {
      const rIdx = Math.floor(Math.random() * (k + 1));
      const tmp = schedule[k]; schedule[k] = schedule[rIdx]; schedule[rIdx] = tmp;
    }
    const start = Date.now();
    try {
      for (const item of schedule) {
        if (cancelRef.current) break;
        const A = players[item.i];
        const B = players[item.j];
        const xIsA = item.xIsA;
        const px = xIsA ? A : B;
        const po = xIsA ? B : A;
        // Generate unique game ID for logging if enabled
        const gameId = enableGameLogging ? `tournament_${Date.now()}_${item.i}_${item.j}_${item.gameIndex}` : null;
        const outcome = await playSingleGame(px, po, boardSize, item.gamePattern || pattern, gameId, (logTag || null));
        if (cancelRef.current) break;
        let scoreA;
        if (outcome.result === 'draw') scoreA = 0.5;
        else if ((outcome.result === 'X' && xIsA) || (outcome.result === 'O' && !xIsA)) scoreA = 1;
        else scoreA = 0;
        const scoreB = 1 - scoreA;
        const expA = expectedScore(r[A.id], r[B.id]);
        const expB = 1 - expA;
        r[A.id] = Math.round(r[A.id] + K_FACTOR * (scoreA - expA));
        r[B.id] = Math.round(r[B.id] + K_FACTOR * (scoreB - expB));
        const winnerIsA = ((outcome.result === 'X' && xIsA) || (outcome.result === 'O' && !xIsA));
        res.push({
          A: A.name,
          B: B.name,
          A_id: A.id,
          B_id: B.id,
          A_icon: (A.iconFile || A.thumbnail || ''),
          B_icon: (B.iconFile || B.thumbnail || ''),
          game: item.gameIndex + 1,
          pattern: item.gamePattern || pattern,
          result: outcome.winnerName,
          winnerSide: winnerIsA ? 'A' : (outcome.result === 'draw' ? 'D' : 'B'),
          plies: outcome.plies,
          A_elo: r[A.id],
          B_elo: r[B.id]
        });
        if (!cancelRef.current) {
          setRatings({ ...r });
          setResults([...res]);
          setProgress(prev => ({ done: prev.done + 1, total: prev.total }));
          setElapsedMs(Date.now() - start);
        }
      }
    } finally {
      if (!cancelRef.current) {
        setRatings(r);
        setResults(res);
        setIsRunning(false);
        setElapsedMs(Date.now() - start);
      }
    }
  };

  const sorted = [...aiPlayersConfig].map(p => ({ id: p.id, name: p.name, rating: ratings[p.id] || 1500 }))
    .sort((a,b) => b.rating - a.rating);

  // Calculate tournament summary statistics
  const firstPlayerWins = results.filter(r => r.winnerSide === 'A').length;
  const secondPlayerWins = results.filter(r => r.winnerSide === 'B').length;
  const draws = results.filter(r => r.winnerSide === 'D').length;
  const totalGames = results.length;
  const firstPlayerWinRate = totalGames > 0 ? ((firstPlayerWins / totalGames) * 100).toFixed(1) : '0.0';
  const secondPlayerWinRate = totalGames > 0 ? ((secondPlayerWins / totalGames) * 100).toFixed(1) : '0.0';
  const drawRate = totalGames > 0 ? ((draws / totalGames) * 100).toFixed(1) : '0.0';

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Tournament (ELO)</h2>
        <button onClick={onBack}>Back to Game</button>
      </div>
      {totalGames > 0 && (
        <div style={{ 
          background: '#f5f5f5', 
          padding: '12px 16px', 
          borderRadius: '8px', 
          marginBottom: '16px',
          border: '1px solid #ddd'
        }}>
          <div style={{ 
            display: 'flex', 
            gap: '24px', 
            alignItems: 'center',
            fontSize: '14px'
          }}>
            <div>
              <strong>Tournament Summary:</strong> {totalGames} games
            </div>
            <div style={{ color: '#2e7d32' }}>
              <strong>1st Player Wins:</strong> {firstPlayerWins} ({firstPlayerWinRate}%)
            </div>
            <div style={{ color: '#d32f2f' }}>
              <strong>2nd Player Wins:</strong> {secondPlayerWins} ({secondPlayerWinRate}%)
            </div>
            <div style={{ color: '#1976d2' }}>
              <strong>Draws:</strong> {draws} ({drawRate}%)
            </div>
            <div style={{ color: '#666' }}>
              <strong>Avg Plies:</strong> {totalGames > 0 ? (results.reduce((sum, r) => sum + r.plies, 0) / totalGames).toFixed(1) : '0'}
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <label>Board Size
          <select value={boardSize} onChange={e => setBoardSize(parseInt(e.target.value))} style={{ marginLeft: 8 }}>
            {[3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}x{n}</option>)}
          </select>
        </label>
        <label>Pattern
          <select value={pattern} onChange={e => setPattern(e.target.value)} disabled={!!patternSet} style={{ marginLeft: 8 }}>
            {['skinny','knobby','f','tic','tee','cross','el','elly','boxy','snaky','tippy','aspy','stair','p','l','y','u','c','s','i']
              .map(pid => <option key={pid} value={pid}>{pid}</option>)}
          </select>
        </label>
        <label>Pattern set
          <select value={patternSet} onChange={e => setPatternSet(e.target.value)} style={{ marginLeft: 8 }}>
            <option value="">(none)</option>
            <option value="tetrominoes">tetrominoes</option>
            <option value="pentominoes">pentominoes</option>
            <option value="hexominoes">hexominoes</option>
          </select>
        </label>
        <label>Games per pairing
          <input type="number" min={1} max={20} value={gamesPerPair} onChange={e => setGamesPerPair(parseInt(e.target.value)||1)} style={{ width: 60, marginLeft: 8 }} />
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label>
            <input 
              type="checkbox" 
              checked={enableGameLogging} 
              onChange={e => setEnableGameLogging(e.target.checked)}
              disabled={isRunning}
            />
            Log games to database (for NN training)
          </label>
          {enableGameLogging && (
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginLeft: 12, marginRight: 6 }}>Tag:</span>
              <input
                type="text"
                maxLength={80}
                placeholder="optional tag"
                value={logTag}
                onChange={e => setLogTag(e.target.value)}
                disabled={isRunning}
                style={{ width: 220 }}
              />
            </label>
          )}
        </div>
        <button onClick={runTournament} disabled={isRunning}>Run Tournament</button>
        {(() => {
          const totalSec = Math.max(0, Math.floor(elapsedMs / 1000));
          const mm = Math.floor(totalSec / 60);
          const ss = totalSec % 60;
          const avg = progress.done > 0 ? (elapsedMs / 1000 / progress.done).toFixed(2) : '0.00';
          return (
            <span>
              {isRunning ? 'Running…' : 'Done.'} {progress.done}/{progress.total}
              {progress.total > 0 && (
                <> — elapsed: {mm}m {ss}s — avg: {avg}s/game</>
              )}
            </span>
          );
        })()}
      </div>

      <h3>Ratings</h3>
      <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 16 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: 6 }}>Rank</th>
            <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: 6 }}>Player</th>
            <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc', padding: 6 }}>ELO</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, idx) => (
            <tr key={row.id}>
              <td style={{ padding: 6 }}>{idx + 1}</td>
              <td style={{ padding: 6 }}>{row.name}</td>
              <td style={{ padding: 6, textAlign: 'right' }}>{row.rating}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Match Log</h3>
      <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #eee' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: 6 }}>Pair</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: 6 }}>Game</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: 6 }}>Result</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: 6 }}>Pattern</th>
              <th style={{ textAlign: 'center', borderBottom: '1px solid #ccc', padding: 6 }}>Winner</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc', padding: 6 }}>Plies</th>
            </tr>
          </thead>
          <tbody>
            {results.map((m, i) => (
              <tr key={i}>
                <td style={{ padding: 6 }}>{m.A} vs {m.B}</td>
                <td style={{ padding: 6 }}>{m.game}</td>
                <td style={{ padding: 6 }}>{m.result}</td>
                <td style={{ padding: 6 }}>{m.pattern}</td>
                <td style={{ padding: 6, textAlign: 'center' }}>
                  {m.winnerSide === 'D' ? 'Draw' : (
                    <img
                      src={`/ai_players/${m.winnerSide === 'A' ? m.A_icon : m.B_icon}`}
                      alt={m.winnerSide === 'A' ? m.A : m.B}
                      title={m.winnerSide === 'A' ? m.A : m.B}
                      style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover' }}
                    />
                  )}
                </td>
                <td style={{ padding: 6, textAlign: 'right' }}>{m.plies}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


